import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  QrCode, Timer, IndianRupee, Sparkles, MapPin, Phone, Mail,
  ScanLine, HandCoins, Armchair, ChevronRight, Star, ShieldCheck, Menu, X, Gift,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1611769423596-698ff0ed299f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const INTERIOR = "https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const steps = [
  { icon: ScanLine, title: "Scan", desc: "Scan the QR code beside your massage chair." },
  { icon: Timer, title: "Choose", desc: "Select 10, 20 or 30 minutes of pure relaxation." },
  { icon: HandCoins, title: "Pay", desc: "Pay securely by Cash or UPI / GPay." },
  { icon: Armchair, title: "Relax", desc: "Show your billing code to staff and unwind." },
];

const faqs = [
  ["How do I start a massage?", "Simply scan the QR code placed beside any King's massage chair, enter your details, choose a duration, and pay. You'll receive a billing code to show our staff."],
  ["What payment methods are accepted?", "We accept Cash at the counter and UPI / Google Pay / PhonePe. Your payment is always verified before your massage begins."],
  ["Is my payment safe?", "Yes. Every payment is verified by our staff or payment gateway before a session starts. A screenshot alone is never accepted as proof."],
  ["Can I get a receipt?", "Absolutely. After a confirmed payment, a digital receipt with your billing code is generated instantly, which you can save or print."],
  ["What are your operating hours?", "Our salons operate from 11:00 AM to 2:00 AM every day across all locations."],
];

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    ["Service", "#service"], ["How It Works", "#how"], ["Pricing", "#pricing"],
    ["Book Online", "#book"], ["Locations", "#locations"], ["FAQs", "#faqs"], ["Contact", "#contact"],
  ];
  return (
    <header className="fixed top-0 z-50 w-full glass border-b border-border">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Logo size="sm" subtitle={false} />
        <nav className="hidden items-center gap-7 lg:flex">
          {links.map(([l, h]) => (
            <a key={h} href={h} className="text-sm text-muted-foreground hover:text-gold transition-colors">{l}</a>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Link to="/login"><Button variant="outline" className="gold-border text-gold hover:bg-gold-muted" data-testid="nav-staff-login">Staff Login</Button></Link>
        </div>
        <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-border px-4 py-4 space-y-3">
          {links.map(([l, h]) => (
            <a key={h} href={h} onClick={() => setOpen(false)} className="block text-sm text-muted-foreground hover:text-gold">{l}</a>
          ))}
          <Link to="/login" className="block"><Button variant="outline" className="w-full gold-border text-gold">Staff Login</Button></Link>
        </div>
      )}
    </header>
  );
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [chairs, setChairs] = useState([]);
  const [cfg, setCfg] = useState({});

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/locations/public").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/chairs/public").then((r) => setChairs(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const chairGroups = [];
  chairs.forEach((c) => {
    let g = chairGroups.find((x) => x.location_id === c.location_id);
    if (!g) { g = { location_id: c.location_id, location_name: c.location_name, chairs: [] }; chairGroups.push(g); }
    g.chairs.push(c);
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />

      {/* Hero */}
      <section className="relative flex min-h-screen items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury massage chair" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-vignette" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl animate-fade-up">
            {cfg.first_time_offer_enabled && (
              <a href="#pricing" data-testid="offer-banner"
                className="mb-6 flex w-fit items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-100 backdrop-blur transition-colors hover:bg-emerald-500/20">
                <Gift size={15} className="text-emerald-400" />
                <span><b>{cfg.first_time_offer_label || "First Visit Special"}</b> — {cfg.first_time_offer_minutes} min for {cfg.currency_symbol || "₹"}{cfg.first_time_offer_price}</span>
                <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] uppercase tracking-wider">New Customers</span>
                <ChevronRight size={14} />
              </a>
            )}
            <div className="mb-5 inline-flex items-center gap-2 rounded-full gold-border px-4 py-1.5 text-xs" style={{ background: "rgba(226,184,87,0.06)" }}>
              <Sparkles size={13} className="gold-text" />
              <span className="overline-label">King's Professional Salon</span>
            </div>
            <h1 className="font-serif text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
              Smart Massage,<br /><span className="gold-gradient-text">Reimagined.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-muted-foreground sm:text-lg">
              {cfg.tagline || "Relax. Recharge. Feel Better."} Scan, pay and unwind on premium massage chairs across our salons.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#book"><Button className="h-12 w-full gap-2 bg-gold px-7 text-base font-semibold text-primary-foreground hover:bg-gold-hover sm:w-auto" data-testid="hero-scan-cta">
                <QrCode size={18} /> Scan To Start Massage
              </Button></a>
              <a href="#pricing"><Button variant="outline" className="h-12 w-full gap-2 gold-border px-7 text-base text-gold hover:bg-gold-muted sm:w-auto" data-testid="hero-services-cta">
                View Services <ChevronRight size={18} />
              </Button></a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><Star size={15} className="gold-text" fill="#E2B857" /> Premium Chairs</div>
              <div className="flex items-center gap-1.5"><ShieldCheck size={15} className="gold-text" /> Secure Payments</div>
              <div className="flex items-center gap-1.5"><Timer size={15} className="gold-text" /> Under 60 Seconds</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service */}
      <section id="service" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="relative">
            <img src={INTERIOR} alt="Salon interior" className="rounded-3xl object-cover gold-border" />
          </div>
          <div>
            <div className="overline-label">The Experience</div>
            <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">A moment of calm, whenever you need it</h2>
            <p className="mt-4 text-muted-foreground">
              King's Smart Massage brings professional-grade massage chairs to our salon locations.
              No appointments, no waiting — just scan, pay and relax. Every session is tracked digitally
              for a seamless, transparent experience.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                [Armchair, "Premium Chairs", "Full-body relaxation technology"],
                [ShieldCheck, "Verified Payments", "Every payment is confirmed"],
                [Timer, "Server-Tracked", "Accurate, fair session timing"],
                [Sparkles, "Hygienic & Clean", "Maintained to salon standards"],
              ].map(([Icon, t, d]) => (
                <div key={t} className="rounded-2xl bg-card p-5 gold-border card-hover">
                  <Icon className="gold-text" size={22} />
                  <div className="mt-3 font-semibold">{t}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-card/40 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <div className="overline-label">Simple & Fast</div>
            <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">How It Works</h2>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl bg-card p-7 gold-border card-hover">
                <div className="absolute right-5 top-5 font-serif text-4xl font-bold text-gold/15">0{i + 1}</div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full gold-border" style={{ background: "rgba(226,184,87,0.08)" }}>
                  <s.icon className="gold-text" size={22} />
                </div>
                <h3 className="mt-5 font-serif text-xl font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28">
        <div className="text-center">
          <div className="overline-label">Transparent Pricing</div>
          <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Choose Your Duration</h2>
          <p className="mt-3 text-muted-foreground">Prices may vary with ongoing salon offers.</p>
        </div>
        <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-3">
          {services.map((s, i) => (
            <div key={s.id} className={`relative rounded-3xl p-8 text-center card-hover ${i === 2 ? "bg-gold-muted gold-border" : "bg-card border border-border"}`}>
              {i === 2 && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-primary-foreground">Most Popular</div>}
              <div className="text-sm text-muted-foreground">{s.name}</div>
              <div className="mt-4 flex items-center justify-center gap-1">
                <span className="font-serif text-5xl font-bold gold-text">{s.duration_minutes}</span>
                <span className="text-sm text-muted-foreground">min</span>
              </div>
              <div className="mt-4 flex flex-col items-center justify-center">
                <div className="flex items-center text-2xl font-bold">
                  <IndianRupee size={20} className="gold-text" />{s.effective_price ?? s.price}
                </div>
                {s.on_offer && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-muted-foreground line-through">₹{s.original_price}</span>
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">OFFER</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm text-muted-foreground">
          Scan the QR code at any chair to begin. No app download required.
        </p>
      </section>

      {/* Book Online — pick a chair */}
      {chairs.length > 0 && (
      <section id="book" className="border-y border-border bg-card/40 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <div className="overline-label">Book Online</div>
            <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Scan &amp; Book a Chair</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Not at the salon yet? Pick any chair below to open its booking page, choose your massage and pay — the same as scanning the QR in person.</p>
          </div>
          <div className="mt-12 space-y-10">
            {chairGroups.map((g) => (
              <div key={g.location_id}>
                <div className="mb-4 flex items-center gap-2">
                  <MapPin size={18} className="gold-text" />
                  <h3 className="font-serif text-xl font-semibold">{g.location_name}</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {g.chairs.map((c) => (
                    c.available ? (
                      <Link key={c.id} to={`/massage/${c.slug}`} data-testid={`home-chair-${c.slug}`}
                        className="group flex items-center justify-between rounded-2xl bg-card p-5 gold-border card-hover transition-transform active:scale-[0.98]">
                        <div>
                          <div className="font-mono text-lg font-bold gold-text">{c.slug}</div>
                          <div className="mt-1 text-xs text-muted-foreground">Tap to book</div>
                        </div>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full gold-border" style={{ background: "rgba(226,184,87,0.08)" }}>
                          <QrCode size={18} className="gold-text" />
                        </div>
                      </Link>
                    ) : (
                      <div key={c.id} data-testid={`home-chair-${c.slug}`}
                        className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5 opacity-60">
                        <div>
                          <div className="font-mono text-lg font-bold text-muted-foreground">{c.slug}</div>
                          <div className="mt-1 text-xs text-muted-foreground">Unavailable</div>
                        </div>
                        <Armchair size={18} className="text-muted-foreground/40" />
                      </div>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Locations */}
      <section id="locations" className="border-y border-border bg-card/40 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <div className="overline-label">Find Us</div>
            <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Our Locations</h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {locations.map((l) => (
              <div key={l.id} className="rounded-2xl bg-card p-7 gold-border card-hover">
                <MapPin className="gold-text" size={22} />
                <h3 className="mt-4 font-serif text-xl font-semibold">{l.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{l.address}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs text-gold">
                  <Armchair size={13} /> {l.chair_count} chair{l.chair_count !== 1 ? "s" : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faqs" className="mx-auto max-w-3xl px-4 py-20 sm:px-6 md:py-28">
        <div className="text-center">
          <div className="overline-label">Good To Know</div>
          <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Frequently Asked</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`} className="border-border">
              <AccordionTrigger className="text-left hover:text-gold" data-testid={`faq-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Contact */}
      <section id="contact" className="border-t border-border bg-card/40 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <div className="overline-label">Get In Touch</div>
              <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Visit King's Professional Salon</h2>
              <p className="mt-4 text-muted-foreground">{cfg.tagline || "Relax. Recharge. Feel Better."}</p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3"><Phone className="gold-text" size={18} /><span>{cfg.phone}</span></div>
                <div className="flex items-center gap-3"><Mail className="gold-text" size={18} /><span>{cfg.email}</span></div>
                <div className="flex items-center gap-3"><MapPin className="gold-text" size={18} /><span>{cfg.address}</span></div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center rounded-3xl bg-gold-muted p-10 text-center gold-border">
              <QrCode className="gold-text" size={48} />
              <h3 className="mt-5 font-serif text-2xl font-bold">Ready to relax?</h3>
              <p className="mt-2 text-sm text-muted-foreground">Look for the QR code beside any King's massage chair and start in seconds.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="h-20 md:hidden" aria-hidden="true" />
      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row">
          <Logo size="sm" />
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} King's Professional Salon. All rights reserved.</p>
          <Link to="/login" className="text-xs text-muted-foreground hover:text-gold">Team Login</Link>
        </div>
      </footer>

      {/* Sticky mobile booking CTA */}
      <a href="#book" className="fixed inset-x-0 bottom-0 z-40 border-t border-border glass px-4 py-3 md:hidden"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }} data-testid="mobile-sticky-cta">
        <Button className="h-12 w-full gap-2 bg-gold text-base font-semibold text-primary-foreground hover:bg-gold-hover">
          <QrCode size={18} /> Book / Scan a Chair
        </Button>
      </a>
    </div>
  );
}
